import Vue from 'vue';

export default [
    {
        path: '/login',
        name: 'login',
        component: () => lazyLoadView(import('@views/auth/login')),
        beforeEnter: loggedInRedirect,
    },
    {
        path: '/logout',
        name: 'logout',
        component: () => lazyLoadView(import('@views/auth/logout')),
    },
    {
        path: '/signup',
        name: 'signup',
        component: () => lazyLoadView(import('@views/auth/signup')),
        beforeEnter: loggedInRedirect,
    },
    {
        path: '/reset-password',
        name: 'reset-password',
        component: () => lazyLoadView(import('@views/auth/reset-password')),
        beforeEnter: loggedInRedirect,
    },
    {
        path: '/change-password/:token',
        name: 'change-password',
        component: () => lazyLoadView(import('@views/auth/change-password')),
        beforeEnter: loggedInRedirect,
    },
    {
        path: '/',
        name: 'home',
        component: () => lazyLoadView(import('@views/home')),
    },
    {
        path: '/search',
        name: 'search',
        component: () => lazyLoadView(import('@views/search')),
    },
    {
        path: '/profile',
        name: 'profile',
        component: () => lazyLoadView(import('@views/profile')),
        beforeEnter: loginRedirect,
    },
    {
        path: '/user/:user/:variant',
        name: 'user',
        component: () => lazyLoadView(import('@views/user')),
    },
    {
        path: '/privacy',
        name: 'privacy',
        component: () => lazyLoadView(import('@views/privacy')),
    },
    {
        path: '/404',
        name: '404',
        component: require('@views/error/404').default,
        // Allow props to be passed to the 404 page through route params, such as `resource` to define what wasn't found.
        props: true,
    },
    {
        path: '/500',
        name: '500',
        component: require('@views/error/500').default,
    },
    // Redirect any unmatched routes to the 404 page. This may require some server configuration to work in production:
    // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
    {
        path: '*',
        redirect: '404',
    },
];

/**
 *
 * Lazy-loads view components, but with better UX. A loading view
 * will be used if the component takes a while to load, falling
 * back to a timeout view in case the page fails to load. You can
 * use this component to lazy-load a route with:
 *
 * component: () => lazyLoadView(import('@views/my-view'))
 *
 * NOTE: Components loaded with this strategy DO NOT have access
 * to in-component guards, such as beforeRouteEnter,
 * beforeRouteUpdate, and beforeRouteLeave. You must either use
 * route-level guards instead or lazy-load the component directly:
 *
 * component: () => import('@views/my-view')
 *
 * @param  {Object} AsyncView The async view component.
 *
 * @return {Promise}
 */
function lazyLoadView (AsyncView) {
    /**
     * Async view component handler.
     */
    const AsyncHandler = () => ({
        component: AsyncView,
        // A component to use while the component is loading.
        loading: require('@views/loading').default,
        // Delay before showing the loading component.
        // Default: 200 (milliseconds).
        delay: 400,
        // A fallback component in case the timeout is exceeded when loading the component.
        error: require('@views/error/timeout').default,
        // Time before giving up trying to load the component.
        // Default: Infinity (milliseconds).
        timeout: 10000,
    });

    return Promise.resolve({
        functional: true,
        render (renderer, { data, children }) {
            // Transparently pass any props or children to the view component.
            return renderer(AsyncHandler, data, children);
        },
    });
}

/**
 * Redirect to login if user is not authenticated.
 *
 * @param  {Object}   to   The route to.
 * @param  {Object}   from The route from
 * @param  {Function} next The next middleware closure.
 *
 * @return void
 */
function loginRedirect (to, from, next) {
    // Determine if the user is authenticated
    if (!Vue.auth.check()) {
        return next('login');
    }

    // Continue as normal
    next();
}

/**
 * Redirect login/signup routes back to homepage if already authenticated.
 *
 * @param  {Object}   to   The route to.
 * @param  {Object}   from The route from
 * @param  {Function} next The next middleware closure.
 *
 * @return void
 */
function loggedInRedirect (to, from, next) {
    // Determine if the user is authenticated
    if (Vue.auth.check()) {
        return next('/');
    }

    // Continue as normal
    next();
}
